import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User } from '../types';

// Define context type
interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  register: (email: string, username: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  updateDisplayName: (displayName: string) => Promise<void>;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: false,
  error: null,
  login: async () => {},
  loginWithGoogle: async () => {},
  register: async () => {},
  logout: async () => {},
  checkAuth: async () => {},
  clearError: () => {},
  updateDisplayName: async () => {},
});

// Custom hook to use auth context
export const useAuth = () => useContext(AuthContext);

// Auth provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  // Verify token and get fresh user data including current subscription status
  const checkAuth = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      // First, validate the token locally (this was the original behavior)
      let userFromToken = null;
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          const now = Math.floor(Date.now() / 1000);
          
          if (payload.exp && payload.exp >= now) {
            // Get last known subscription tier from localStorage
            const storedTier = localStorage.getItem('subscription_tier');
            const validTiers = ['free', 'plus', 'premium'];
            const lastKnownTier = validTiers.includes(storedTier || '') ? storedTier : null;
            
            // Token format valid and not expired
            userFromToken = {
              id: payload.sub || '',
              email: payload.email || '',
              username: payload.username || '',
              displayName: payload.displayName || '',
              verified: payload.verified || false,
              role: 'user' as const,
              status: 'active' as const,
              subscriptionTier: (lastKnownTier as 'free' | 'plus' | 'premium') || 'free',
              profileVisibility: 'public' as const,
              showListeningActivity: true,
              allowDirectMessages: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
          } else {
            throw new Error('Token expired');
          }
        } else {
          throw new Error('Invalid token format');
        }
      } catch (tokenError) {
        console.log('Token validation failed:', tokenError);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('subscription_tier');
        setUser(null);
        setLoading(false);
        return;
      }

      // Set user from token first (so user doesn't see loading state for too long)
      // But only if we don't have a stored subscription tier, to avoid flicker
      const storedTierExists = localStorage.getItem('subscription_tier');
      if (!storedTierExists) {
        setUser(userFromToken);
      }

      // Now try to enhance with fresh data from API (subscription status, etc.)
      try {
        const apiBase = import.meta.env.VITE_API_BASE || '/api';
        const response = await fetch(`${apiBase}/auth`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          // Store the fresh subscription tier in localStorage for next time
          if (data.user?.subscriptionTier) {
            localStorage.setItem('subscription_tier', data.user.subscriptionTier);
          }
          // Update user with fresh data from API
          setUser(data.user);
        } else {
          // If API call fails, use the user from token (with stored tier if available)
          console.log('API call failed, using token data:', response.status);
          setUser(userFromToken);
        }
      } catch (apiError) {
        // Network error or other API issue - just use token data
        console.log('API call error, using token data:', apiError);
        setUser(userFromToken);
      }
    } catch (err) {
      console.error('Auth check error:', err);
      localStorage.removeItem('auth_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Register a new user
  const register = async (email: string, username: string, password: string, displayName?: string, turnstileToken?: string) => {
    setLoading(true);
    setError(null);
    try {
      const apiBase = import.meta.env.VITE_API_BASE || '/api';
      const response = await fetch(`${apiBase}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, username, password, displayName, turnstileToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Registration failed');
      }

      // Auto-login after successful registration
      await login(email, password);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'Registration failed');
      setLoading(false);
    }
  };

  // Login user
  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const apiBase = import.meta.env.VITE_API_BASE || '/api';
      const response = await fetch(`${apiBase}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Login failed');
      }

      const data = await response.json();
      localStorage.setItem('auth_token', data.token);
      // Store subscription tier for future page loads
      if (data.user?.subscriptionTier) {
        localStorage.setItem('subscription_tier', data.user.subscriptionTier);
      }
      setUser(data.user);
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Login with Google
  const loginWithGoogle = async (idToken: string) => {
    setLoading(true);
    setError(null);
    try {
      const apiBase = import.meta.env.VITE_API_BASE || '/api';
      const response = await fetch(`${apiBase}/auth/google/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: idToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Google login failed');
      }

      const data = await response.json();
      localStorage.setItem('auth_token', data.token);
      // Store subscription tier for future page loads
      if (data.user?.subscriptionTier) {
        localStorage.setItem('subscription_tier', data.user.subscriptionTier);
      }
      setUser(data.user);
    } catch (err) {
      console.error('Google login error:', err);
      setError(err instanceof Error ? err.message : 'Google login failed');
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = async () => {
    setLoading(true);
    try {
      // Note: Server-side logout is not implemented yet
      // For now, we just clear the local token and call Google logout
      
      // Try to import and use googleLogout if available
      try {
        // This is a dynamic import to avoid requiring the package if not used
        const { googleLogout } = await import('@react-oauth/google');
        googleLogout();
      } catch (googleError) {
        // Ignore errors if googleLogout is not available
        console.log('Google logout not available or not needed');
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('subscription_tier');
      setUser(null);
      setLoading(false);
    }
  };

  // Clear error
  const clearError = () => setError(null);
  
  // Update user's display name
  const updateDisplayName = async (displayName: string) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const apiBase = import.meta.env.VITE_API_BASE || '/api';
      const response = await fetch(`${apiBase}/users/profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ displayName }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update display name');
      }
      
      const data = await response.json();
      setUser(data.user);
    } catch (err) {
      console.error('Update display name error:', err);
      setError(err instanceof Error ? err.message : 'Failed to update display name');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        loginWithGoogle,
        register,
        logout,
        checkAuth,
        clearError,
        updateDisplayName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
